import * as signalR from '@microsoft/signalr';
import { getUrl } from './Conections';
import { FEVRIPSNotification } from '../../types/types.preorders';
import { FacturaNotification } from '../../types/types.facturacion';

let connection: signalR.HubConnection | null = null;
let connectingPromise: Promise<void> | null = null;
let currentPreordenGroup: string | null = null;
let currentSedeGroup: string | null = null;
let currentFacturaGroup: string | null = null;
let notificationCallback: ((notification: FEVRIPSNotification) => void) | null = null;
let contractNotificationCallback: ((notification: FacturaNotification) => void) | null = null;
// Varios suscriptores simultáneos (listado + modal de detalle) sin pisarse entre sí
const contractListeners = new Set<(notification: FacturaNotification) => void>();

/**
 * Actualizar el callback de notificaciones (útil cuando el componente se re-renderiza)
 */
export const updateNotificationCallback = (callback: (notification: FEVRIPSNotification) => void): void => {
  notificationCallback = callback;
};

/**
 * Actualizar el callback de notificaciones de facturas por contrato (módulo Facturación)
 */
export const updateContractNotificationCallback = (callback: ((notification: FacturaNotification) => void) | null): void => {
  contractNotificationCallback = callback;
};

/**
 * Agrega un listener de notificaciones de facturas por contrato. Devuelve la función para
 * removerlo. A diferencia del callback único, soporta varios suscriptores a la vez.
 */
export const addContractNotificationListener = (listener: (notification: FacturaNotification) => void): (() => void) => {
  contractListeners.add(listener);
  return () => {
    contractListeners.delete(listener);
  };
};

/**
 * Garantiza que exista una conexión activa al hub (reutiliza la conexión o el intento en curso).
 * Evita el silencio de subscribeToFactura/subscribeToPreorden cuando el hub aún está conectando.
 */
export const ensureFEVRIPSConnection = async (sedeId?: number | null): Promise<void> => {
  if (connection?.state === signalR.HubConnectionState.Connected) {
    return;
  }
  if (!connectingPromise) {
    connectingPromise = connectToFEVRIPSHub(() => {}, sedeId).finally(() => {
      connectingPromise = null;
    });
  }
  return connectingPromise;
};

/**
 * Conecta al Hub de FEV/RIPS para recibir notificaciones en tiempo real
 */
export const connectToFEVRIPSHub = async (onNotification: (notification: FEVRIPSNotification) => void, sedeId?: number | null): Promise<void> => {
  const url = await getUrl();

  // Guardar el callback
  notificationCallback = onNotification;

  return new Promise((resolve, reject) => {
    connection = new signalR.HubConnectionBuilder().withUrl(`${url}/fevRipsHub`).withAutomaticReconnect([0, 2000, 5000, 10000, 30000]).configureLogging(signalR.LogLevel.Information).build();

    // Listener para notificaciones de facturación
    connection.on('ReceiveInvoiceNotification', (notification: FEVRIPSNotification) => {
      if (notificationCallback) {
        notificationCallback(notification);
      }
    });

    // Listener para notificaciones de facturas por contrato (módulo Facturación)
    connection.on('ReceiveContractInvoiceNotification', (notification: FacturaNotification) => {
      if (contractNotificationCallback) {
        contractNotificationCallback(notification);
      }
      contractListeners.forEach((listener) => {
        try {
          listener(notification);
        } catch {
          // un listener roto no debe tumbar a los demás
        }
      });
    });

    connection
      .start()
      .then(async () => {
        // Conectar al grupo de la sede si se proporciona sedeId
        if (sedeId) {
          try {
            await connection?.invoke('ConnectToSedeGroup', sedeId.toString());
            currentSedeGroup = `FEV_RIPS_${sedeId}`;
          } catch (err) {
            // Silently fail
          }
        }

        resolve();
      })
      .catch((err) => {
        reject(err);
      });

    // Manejo de reconexión
    connection.onreconnecting(() => {});

    connection.onreconnected(() => {
      // Re-suscribirse a los grupos después de reconectar
      if (currentSedeGroup) {
        const sedeId = currentSedeGroup.replace('FEV_RIPS_', '');
        connection?.invoke('ConnectToSedeGroup', sedeId);
      }
      if (currentPreordenGroup) {
        const preordenId = currentPreordenGroup.replace('Preorden_', '');
        connection?.invoke('ConnectToPreordenGroup', preordenId);
      }
      if (currentFacturaGroup) {
        const facturaId = currentFacturaGroup.replace('Factura_', '');
        connection?.invoke('ConnectToFacturaGroup', facturaId);
      }
    });

    connection.onclose(() => {});
  });
};

/**
 * Suscribirse a las notificaciones de una preorden específica
 */
export const subscribeToPreorden = async (idPreorden: number): Promise<void> => {
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
    return;
  }

  try {
    await connection.invoke('ConnectToPreordenGroup', idPreorden.toString());
    currentPreordenGroup = `Preorden_${idPreorden}`;
  } catch (err) {
    // Silently fail
  }
};

/**
 * Desuscribirse de las notificaciones de una preorden específica
 */
export const unsubscribeFromPreorden = async (idPreorden: number): Promise<void> => {
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
    return;
  }

  try {
    await connection.invoke('DisconnectFromPreordenGroup', idPreorden.toString());
    currentPreordenGroup = null;
  } catch (err) {
    // Silently fail
  }
};

/**
 * Suscribirse a las notificaciones de una factura por contrato específica
 */
export const subscribeToFactura = async (idFactura: number): Promise<void> => {
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
    return;
  }

  try {
    await connection.invoke('ConnectToFacturaGroup', idFactura.toString());
    currentFacturaGroup = `Factura_${idFactura}`;
  } catch (err) {
    // Silently fail
  }
};

/**
 * Desuscribirse de las notificaciones de una factura por contrato específica
 */
export const unsubscribeFromFactura = async (idFactura: number): Promise<void> => {
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
    return;
  }

  try {
    await connection.invoke('DisconnectFromFacturaGroup', idFactura.toString());
    currentFacturaGroup = null;
  } catch (err) {
    // Silently fail
  }
};

/**
 * Desconectar del Hub FEV/RIPS
 */
export const disconnectFromFEVRIPSHub = async (): Promise<void> => {
  if (!connection) {
    return;
  }

  try {
    // Desuscribirse de los grupos antes de desconectar
    if (currentSedeGroup) {
      const sedeId = currentSedeGroup.replace('FEV_RIPS_', '');
      await connection.invoke('DisconnectFromSedeGroup', sedeId);
    }
    if (currentPreordenGroup) {
      const preordenId = currentPreordenGroup.replace('Preorden_', '');
      await connection.invoke('DisconnectFromPreordenGroup', preordenId);
    }

    await connection.stop();
    connection = null;
    currentSedeGroup = null;
    currentPreordenGroup = null;
  } catch (err) {
    // Silently fail
  }
};

/**
 * Verificar si hay conexión activa
 */
export const isConnected = (): boolean => {
  return connection?.state === signalR.HubConnectionState.Connected;
};

/**
 * Obtener el estado de la conexión
 */
export const getConnectionState = (): signalR.HubConnectionState | null => {
  return connection?.state ?? null;
};
